import { clearGraphDataCache } from './graphDataCache';
import { CAPABILITIES } from '../security/permissions';

const AUTH_STORAGE_KEY = 'aml_engine_is_logged_in';
const AUTH_ROLE_STORAGE_KEY = 'aml_engine_user_role';
const AUTH_ROLES_STORAGE_KEY = 'aml_engine_user_roles';
const AUTH_ORGANIZATION_IDS_STORAGE_KEY = 'aml_engine_organization_ids';
const AUTH_CLIENT_ID_STORAGE_KEY = 'aml_engine_client_id';
const AUTH_PERMISSIONS_STORAGE_KEY = 'aml_engine_user_permissions';

export const USER_ROLES = {
  ROOT: 'ROOT',
  SUPERADMIN: 'SUPERADMIN',
  ADMIN: 'ADMIN',
  AUDIT: 'AUDIT',
  MANAGER: 'MANAGER',
  ANALYST: 'ANALYST',
  MONITOR: 'MONITOR',
};

export const READ_ADMIN_ROLES = [
  USER_ROLES.ROOT,
  USER_ROLES.SUPERADMIN,
  USER_ROLES.ADMIN,
  USER_ROLES.AUDIT,
  USER_ROLES.MANAGER,
  USER_ROLES.ANALYST,
];

export const POLICY_ADMIN_ROLES = [
  USER_ROLES.ROOT,
  USER_ROLES.SUPERADMIN,
  USER_ROLES.ADMIN,
  USER_ROLES.MANAGER,
];

const DEFAULT_ROLE = USER_ROLES.MONITOR;

const normalizeEnvCredential = (value) => (typeof value === 'string' ? value.trim() : '');
const normalizeRole = (value) => normalizeEnvCredential(value).toUpperCase();

const resolveConfiguredRole = () => DEFAULT_ROLE;

export const isProductionEnvironment = () => process.env.NODE_ENV === 'production';

export const isAuthenticationEnabled = () => true;

export const isUserAuthenticated = () => localStorage.getItem(AUTH_STORAGE_KEY) === 'true';

export const getCurrentUserRole = () => {
  const storedRole = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY));

  if (Object.values(USER_ROLES).includes(storedRole)) {
    return storedRole;
  }

  return resolveConfiguredRole();
};

export const getCurrentUserRoles = () => {
  try {
    const storedRoles = JSON.parse(localStorage.getItem(AUTH_ROLES_STORAGE_KEY) || '[]');
    return Array.isArray(storedRoles) ? storedRoles.filter((role) => Object.values(USER_ROLES).includes(role)) : [];
  } catch {
    return [];
  }
};

export const getCurrentOrganizationIds = () => {
  try {
    const storedOrganizations = JSON.parse(localStorage.getItem(AUTH_ORGANIZATION_IDS_STORAGE_KEY) || '[]');
    return Array.isArray(storedOrganizations) ? storedOrganizations : [];
  } catch {
    return [];
  }
};

export const hasAnyRole = (allowedRoles = []) => {
  if (!Array.isArray(allowedRoles) || !allowedRoles.length) {
    return true;
  }

  const currentRoles = getCurrentUserRoles();
  return allowedRoles.some((role) => currentRoles.includes(role) || role === getCurrentUserRole());
};

export const getCurrentClientId = () => normalizeEnvCredential(localStorage.getItem(AUTH_CLIENT_ID_STORAGE_KEY));

export const getCurrentUserPermissions = () => {
  try {
    const storedPermissions = JSON.parse(localStorage.getItem(AUTH_PERMISSIONS_STORAGE_KEY) || '[]');
    return Array.isArray(storedPermissions) ? storedPermissions.filter((permission) => typeof permission === 'string') : [];
  } catch {
    return [];
  }
};

export const canAccessReadAdminArea = () => hasAnyRole(READ_ADMIN_ROLES);

// Fase 2 (enforced) do rollout de RBAC dinâmico (ver docs/rbac/README.md no
// rules-engine): quem decide é o claim `permissions` do JWT, não mais o
// papel. `ROLE_CAPABILITIES` foi removido de security/permissions.js.
export const hasCapability = (capability) => getCurrentUserPermissions().includes(capability);

export const hasAllCapabilities = (requiredCapabilities = []) => requiredCapabilities.every((capability) => hasCapability(capability));

export const canViewPolicyRules = () => hasCapability(CAPABILITIES.POLICY_RULES_VIEW);

export const canManagePolicyRules = () => hasCapability(CAPABILITIES.POLICY_RULES_MANAGE);

export const canViewTransactions = () => hasCapability(CAPABILITIES.TRANSACTIONS_VIEW);

export const canViewTransactionDetails = () => hasCapability(CAPABILITIES.TRANSACTIONS_DETAILS_VIEW);

export const canViewTransactionStatus = () => hasCapability(CAPABILITIES.TRANSACTIONS_STATUS_VIEW);

export const canViewAdminPlans = () => hasCapability(CAPABILITIES.ADMIN_PLANS_VIEW);

export const canManageAdminPlans = () => hasCapability(CAPABILITIES.ADMIN_PLANS_MANAGE);

export const canViewAdminTenancy = () => hasCapability(CAPABILITIES.TENANCY_VIEW);

export const canManageAdminOrganizations = () => hasCapability(CAPABILITIES.TENANCY_ORGANIZATIONS_MANAGE);

export const canManageAdminClients = () => hasCapability(CAPABILITIES.TENANCY_CLIENTS_MANAGE);

export const canViewAdminUsers = () => hasCapability(CAPABILITIES.USERS_VIEW);

export const canCreateAdminUsers = () => hasCapability(CAPABILITIES.USERS_CREATE);

export const canManageAdminUsers = () => hasCapability(CAPABILITIES.USERS_UPDATE);

export const setUserAuthenticated = (roleOrRoles, clientId = '', organizationIds = [], permissions = []) => {
  const roles = (Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles])
    .map(normalizeRole)
    .filter((role) => Object.values(USER_ROLES).includes(role));
  const normalizedRole = roles[0] || resolveConfiguredRole();
  const roleToPersist = Object.values(USER_ROLES).includes(normalizedRole)
    ? normalizedRole
    : resolveConfiguredRole();
  const normalizedClientId = normalizeEnvCredential(clientId);

  localStorage.setItem(AUTH_STORAGE_KEY, 'true');
  localStorage.setItem(AUTH_ROLE_STORAGE_KEY, roleToPersist);
  localStorage.setItem(AUTH_ROLES_STORAGE_KEY, JSON.stringify(roles.length ? roles : [roleToPersist]));
  localStorage.setItem(AUTH_ORGANIZATION_IDS_STORAGE_KEY, JSON.stringify(organizationIds));
  localStorage.setItem(AUTH_PERMISSIONS_STORAGE_KEY, JSON.stringify(Array.isArray(permissions) ? permissions : []));
  if (normalizedClientId) {
    localStorage.setItem(AUTH_CLIENT_ID_STORAGE_KEY, normalizedClientId);
  } else {
    localStorage.removeItem(AUTH_CLIENT_ID_STORAGE_KEY);
  }
};

export const clearUserAuthenticated = () => {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(AUTH_ROLE_STORAGE_KEY);
  localStorage.removeItem(AUTH_ROLES_STORAGE_KEY);
  localStorage.removeItem(AUTH_ORGANIZATION_IDS_STORAGE_KEY);
  localStorage.removeItem(AUTH_CLIENT_ID_STORAGE_KEY);
  localStorage.removeItem(AUTH_PERMISSIONS_STORAGE_KEY);
  clearGraphDataCache();
};
